import { Box, Chip, Tooltip } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import React from 'react';

// -- type definitions --

export type FilterOption = {
  key: string;
  label: string;
  value: string;
};

export type FilterDefinition = {
  key: string;
  label: string;
};

// -- helper functions --

/**
 * Keeps only one copy of each value in a list.
 */
export function uniqueValues<T>(values: readonly T[]): T[] {
  return Array.from(new Set(values));
}

/**
 * Turns a list of filter chips into a record keyed by filter name.
 */
export function toFilterRecord(
  options: readonly FilterOption[]
): Record<string, string[]> {
  return options.reduce<Record<string, string[]>>((accumulator, option) => {
    const currentValues = accumulator[option.key] ?? [];

    if (!currentValues.includes(option.value)) {
      accumulator[option.key] = [...currentValues, option.value];
    }

    return accumulator;
  }, {});
}

/**
 * Adds selected items that are missing from the available list so they stay visible.
 */
export function mergeOptionsByKey<T>(
  availableOptions: readonly T[],
  selectedOptions: readonly T[],
  isSameOption: (left: T, right: T) => boolean
): T[] {
  const mergedOptions = [...availableOptions];

  selectedOptions.forEach((selectedOption) => {
    if (!mergedOptions.some((option) => isSameOption(option, selectedOption))) {
      mergedOptions.push(selectedOption);
    }
  });

  return mergedOptions;
}

/**
 * Returns true when two filter chip options describe the same choice.
 */
export function sameFilterOption(
  left: FilterOption,
  right: FilterOption
): boolean {
  return left.key === right.key && left.value === right.value;
}

const GROUP_LABEL_FR: Record<string, string> = {
  project: 'projet',
  site: 'site',
  device_type: 'type de capteur',
  device: 'capteur',
  unit: 'unité',
  thematic: 'thématique',
  physical_quantity: 'grandeur physique',
  physical_size: 'grandeur physique',
  timezone: 'fuseau horaire',
};

/** French labels for common coupled-dimension group names. */
export const COUPLED_GROUP_LABEL_FR: Record<string, string> = {
  wind_data: 'Capteur de vent',
  climate_data: 'Capteur climatique',
};

/**
 * Returns a display label for a coupled dimension group.
 */
export function getCoupledGroupLabel(groupKey: string): string {
  return COUPLED_GROUP_LABEL_FR[groupKey] ?? groupKey;
}

/** French labels for common independent-dimension slot names. */
export const INDEPENDENT_DIMENSION_LABEL_FR: Record<string, string> = {
  unit_x: 'Abscisse (X)',
  unit_y: 'Ordonnée (Y)',
  unique: 'Sources',
  data_series: 'Séries',
  columns: 'Colonnes',
};

/**
 * Returns a display label for an independent dimension slot.
 */
export function getIndependentDimensionLabel(dimensionKey: string): string {
  return INDEPENDENT_DIMENSION_LABEL_FR[dimensionKey] ?? dimensionKey;
}

/**
 * Filters autocomplete options by case-insensitive substring match on labels.
 */
export function filterOptionsByInputLabel<T extends { label: string }>(
  options: T[],
  state: { inputValue: string }
): T[] {
  const normalizedInput = state.inputValue.trim().toLowerCase();

  if (!normalizedInput) {
    return options;
  }

  return options.filter((option) =>
    option.label.toLowerCase().includes(normalizedInput)
  );
}

export const filterOptionsByFilterChipLabel = createFilterOptions<FilterOption>(
  {
    stringify: (option) => getFilterOptionLabel(option),
  }
);

/**
 * Normalizes a token for stable filter key matching.
 */
export function normalizeToken(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Returns a French label for a filter group when one is known.
 */
export function toFrenchGroupLabel(rawLabel: string, key?: string): string {
  const normalizedKey = key ? normalizeToken(key) : '';
  const normalizedLabel = normalizeToken(rawLabel);

  return (
    GROUP_LABEL_FR[normalizedKey] ?? GROUP_LABEL_FR[normalizedLabel] ?? rawLabel
  );
}

/**
 * Builds the visible label for one filter chip.
 */
export function getFilterOptionLabel(option: FilterOption): string {
  return `${toFrenchGroupLabel(option.label, option.key)}: ${option.value}`;
}

/**
 * Renders one autocomplete option with ellipsis and a hover tooltip.
 *
 * MUI Autocomplete passes a frozen props object (including ref). We must peel
 * off key/ref before spreading to avoid "can't define property current" errors.
 */
export function renderEllipsizedOption(
  props: React.HTMLAttributes<HTMLLIElement> & {
    key?: React.Key;
    ref?: React.Ref<HTMLLIElement>;
  },
  label: string
): React.ReactNode {
  const { key, ref, ...rest } = props;
  const optionProps = { ...rest };

  return (
    <Box
      component="li"
      key={key}
      {...optionProps}
      ref={ref}
      sx={{
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      <Tooltip title={label} placement="top-start">
        <span>{label}</span>
      </Tooltip>
    </Box>
  );
}

/**
 * Renders chips for selected filter options.
 */
export function renderFilterTags(
  selectedOptions: readonly FilterOption[],
  getTagProps: (params: { index: number }) => Record<string, unknown>
): React.ReactNode {
  return selectedOptions.map((option, index) => {
    const label = getFilterOptionLabel(option);
    const tagProps = getTagProps({ index });
    const key = String(
      tagProps.key ?? `${option.key}:${option.value}:${index}`
    );
    const {
      key: _tagKey,
      ref: _ref,
      ...chipProps
    } = tagProps as {
      key?: React.Key;
      ref?: React.Ref<unknown>;
      [key: string]: unknown;
    };

    return (
      <Tooltip key={key} title={label} placement="top-start">
        <Chip
          {...chipProps}
          label={label}
          size="small"
          sx={{ maxWidth: 260 }}
        />
      </Tooltip>
    );
  });
}

/**
 * Renders chips for selected items that expose a label field.
 */
export function renderLabelTags<T extends { label: string }>(
  selectedItems: readonly T[],
  getTagProps: (params: { index: number }) => Record<string, unknown>,
  getItemKey: (item: T) => string
): React.ReactNode {
  return selectedItems.map((item, index) => {
    const tagProps = getTagProps({ index });
    const key = String(tagProps.key ?? getItemKey(item));
    const {
      key: _tagKey,
      ref: _ref,
      ...chipProps
    } = tagProps as {
      key?: React.Key;
      ref?: React.Ref<unknown>;
      [key: string]: unknown;
    };

    return (
      <Tooltip key={key} title={item.label} placement="top">
        <Chip
          {...chipProps}
          label={item.label}
          size="small"
          sx={{ maxWidth: 260 }}
        />
      </Tooltip>
    );
  });
}

/**
 * Shared styling for selector panels that match the control panel look.
 */
export const controlPanelSelectorBoxSx = {
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  bgcolor: 'background.paper',
  px: 1.5,
  py: 0.75,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  '& .MuiInputBase-root': {
    minHeight: 36,
  },
  '& .MuiOutlinedInput-input': {
    py: 0.75,
  },
  '& .MuiAutocomplete-tag': {
    height: 22,
  },
} as const;

/**
 * Keeps only filter values that are still valid after earlier filters changed.
 */
export function sanitizeFiltersByHierarchy(
  getAllowedValues: (
    definitionIndex: number,
    activeFilters: Record<string, string[]>
  ) => readonly string[],
  definitions: readonly FilterDefinition[],
  candidateFilters: Record<string, string[]>
): Record<string, string[]> {
  const sanitizedFilters: Record<string, string[]> = {};

  definitions.forEach((definition, index) => {
    const requestedValues = candidateFilters[definition.key] ?? [];

    if (requestedValues.length === 0) {
      return;
    }

    const allowedOptions = getAllowedValues(index, sanitizedFilters);
    const keptValues = requestedValues.filter((value) =>
      allowedOptions.includes(value)
    );

    if (keptValues.length > 0) {
      sanitizedFilters[definition.key] = keptValues;
    }
  });

  return sanitizedFilters;
}

/**
 * Returns true when a record matches every active filter.
 */
export function matchesSelectedFilters(
  dimensions: Record<string, string>,
  filters: Record<string, string[]>,
  definitions: readonly FilterDefinition[]
): boolean {
  return definitions.every((definition) => {
    const selectedValues = filters[definition.key] ?? [];

    if (selectedValues.length === 0) {
      return true;
    }

    return selectedValues.includes(dimensions[definition.key] ?? '');
  });
}

/**
 * Returns distinct filter values available at one hierarchy level.
 */
export function getFilterOptionsAt(
  records: readonly { dimensions: Record<string, string> }[],
  definitions: readonly FilterDefinition[],
  filters: Record<string, string[]>,
  index: number
): string[] {
  const scopedDefinitions = definitions.slice(0, index);
  const relevantRecords = records.filter((record) =>
    matchesSelectedFilters(record.dimensions, filters, scopedDefinitions)
  );

  return uniqueValues(
    relevantRecords
      .map((record) => record.dimensions[definitions[index]?.key ?? ''])
      .filter((value): value is string => Boolean(value))
  );
}
